import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Host,
  Injector,
  Input,
  OnInit,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { UserProfileServiceApi } from '../../../common-api/system-management-api/api/userProfile.service';
import { LocalStorageService } from '../../../common/services/local-storage.service';
import { TableCell } from '../../../core/models/span-table';
import { HintDialogService } from '../../../core/services';
import { isNullOrEmpty } from '../../../core/utils';
import { DataTableComponent } from '../../../shared/components/data-table/data-table.component';

@Component({
  selector: 'sigv-input-data-table',
  templateUrl: './input-data-table.component.html',
  styleUrls: ['./input-data-table.component.scss'],
})
export class InputDataTableComponent
  extends DataTableComponent
  implements OnInit
{
  /**
   * 欲進行rowSpan或colspan的Header
   */
  @Input() spanHeaderColumns: TableCell[] = [];
  @Input() useTimeoutChecker = false;
  /**
   * 遞迴產生的 table 需透過此參數知道要新增的資料是針對哪一個 index 的 table
   */
  @Input() index: number = 0;
  /**
   * 是否需顯示刪除前確認訊息
   */
  @Input() showDeleteConfirmation: boolean = false;
  /**
   * 刪除前確認訊息
   */
  @Input() deleteConfirmationMsg: string = '';
  /**
   * 新增按鈕 是否disabled(預設false)
   */
  @Input() isDisabledAddButton: boolean = false;
  /**
   * 新增按鈕 style
   */
  @Input() addButtonStyle: string = 'p-button-secondary p-content-button';
  /**
   * 新增按鈕 style
   */
  @Input() editCancelAlert: boolean = false;
  /**
   * 新增後是否可以直接編輯（不需要再按一次鉛筆icon）
   */
  @Input() canDirectEdit: boolean = false;
  /**
   * 是否執行刪除檢核
   */
  @Input() validateDelete: boolean = false;
  /**
   * 是否唯讀
   */
  @Input() isReadOnly: boolean = false;
  /**
   * row edit cancel 例外處理
   */
  @Input() skipReplacingIndex: boolean = false;

  /**
   * 檢核新增資料列方法
   */
  @Input() validateRowEditFuncWithSync!: (
    row: any,
    index: number
  ) => Promise<boolean>;
  originalClonedRows: { [s: string]: any } = {}; // 用來存放原始資料

  constructor(
    @Host() el: ElementRef,
    injector: Injector,
    cd: ChangeDetectorRef,
    localStorageService: LocalStorageService,
    userProfileService: UserProfileServiceApi,
    private hintDialogService: HintDialogService,
    translate: TranslateService
  ) {
    super(el, injector, cd, localStorageService, userProfileService, translate);
  }

  /**
   * 編輯資料列(列)
   * button click event
   * @param {*} row
   * @param {number} index
   * @memberof DataTableComponent
   */
  useOnRowEditInit(row: any, index: number): void {
    const useDataKey = this.dataKey.split('.')[1];
    this.logger.debug('onRowEditInit', row);
    this.clonedRows[row.controls[useDataKey]?.getRawValue()] = {
      ...row?.getRawValue(),
    };
    this.logger.debug(
      'onRowEditInit',
      this.clonedRows[row.controls[useDataKey]?.getRawValue()]
    );
    row.editing = true;
    this.logger.debug('useOnRowEditInit editing', row.editing);

    this.rowEdit.emit(row);
  }
  /**
   * 儲存編輯資料列
   * button click event
   * @param {*} row
   * @param {number} index
   * @return {*}  {void}
   * @memberof DataTableComponent
   */
  useOnRowEditSave(row: any, index: number): void {
    const useDataKey = this.dataKey.split('.')[1];
    this.logger.debug('onRowEditSave', row);
    if (this.canDirectEdit) {
      // 編輯檢核失敗不關閉
      if (this.validateRowEditFunc(row, index) === false) {
        this.table.initRowEdit(row);
        row.editing = true;

        return;
      } else {
        row.editing = false;
        row.create = false;
      }
    } else {
      // 編輯檢核失敗不關閉
      if (this.validateRowEditFunc(row, index) === false) {
        this.table.initRowEdit(row);
        row.editing = true;
        return;
      } else {
        row.editing = false;
      }
    }

    delete this.clonedRows[row.controls[useDataKey]?.getRawValue()];
    this.logger.debug('onRowEditSave', this.value, row);
    this.logger.debug('onRowEditSave editing', row.editing);

    this.valueChange.emit(this.value);
    this.rowEditSave.emit(row);
  }
  /**
   * 儲存編輯資料列
   * button click event
   * @param {*} row
   * @param {number} index
   * @return {*}  {void}
   * @memberof DataTableComponent
   */
  async useOnRowEditSaveWithSync(row: any, index: number): Promise<void> {
    const useDataKey = this.dataKey.split('.')[1];
    this.logger.debug('onRowEditSave', row);
    if (this.canDirectEdit) {
      // 編輯檢核失敗不關閉
      if ((await this.validateRowEditFuncWithSync(row, index)) === false) {
        this.table.initRowEdit(row);
        row.editing = true;

        return;
      } else {
        row.editing = false;
        row.create = false;
      }
    } else {
      // 編輯檢核失敗不關閉
      if ((await this.validateRowEditFuncWithSync(row, index)) === false) {
        this.table.initRowEdit(row);
        row.editing = true;
        return;
      } else {
        row.editing = false;
      }
    }

    delete this.clonedRows[row.controls[useDataKey]?.getRawValue()];
    this.logger.debug('onRowEditSave', this.value, row);
    this.logger.debug('onRowEditSave editing', row.editing);

    this.valueChange.emit(this.value);
    this.rowEditSave.emit(row);
  }

  /**
   * 取消編輯資料列
   * button click event
   * @param {*} row
   * @param {number} index
   * @memberof DataTableComponent
   */
  useOnRowEditCancel(row: any, index: number, skipAlert?: boolean): void {
    let currentPageIndex = index % this.table._rows;
    if (currentPageIndex < 0) {
      currentPageIndex = 0;
    }
    if (this.skipReplacingIndex) {
      currentPageIndex = index;
    }
    this.logger.debug('useOnRowEditCancel emit', row);
    if (row.create && this.canDirectEdit) {
      this.logger.debug('onRowEditCancel', row);
      this.value[currentPageIndex] = this.clonedRows[row[this.dataKey]];
      delete this.clonedRows[row[this.dataKey]];
      this.cd.detectChanges();
      this.valueChange.emit(this.value);
      this.rowEditCancel.emit(row);
      return;
    }
    this.valueChange.emit(this.value);
    this.rowEditCancel.emit(row);
    const rollBackValue = () => {
      row.editing = false;
      this.value[currentPageIndex].setValue(
        this.clonedRows[row.controls[useDataKey]?.getRawValue()]
      );
      delete this.clonedRows[row.controls[useDataKey]?.getRawValue()];
      this.logger.debug('useOnRowEditCancel emit', row);
      this.valueChange.emit(this.value);
      this.rowEditCancel.emit(row);
      return;
    };
    const backToEdit = () => {
      row.editing = true;
      this.logger.debug('onRowEditCancel editing backtoedit', row.editing);
      this.logger.debug('onRowEditCancel-cancel-callback');
      this.table.initRowEdit(row);
    };
    if (!skipAlert) skipAlert = false;
    const useDataKey = this.dataKey.split('.')[1];
    this.logger.debug('onRowEditCancel', row);
    this.logger.debug(
      'onRowEditCancel',
      this.clonedRows[row.controls[useDataKey]],
      useDataKey
    );
    if (!skipAlert && this.editCancelAlert) {
      row.editing = false;
      this.logger.debug(
        'onRowEditCancel-skip',
        this.value[currentPageIndex]?.getRawValue(),
        currentPageIndex,
        this.value
      );
      // 先儲存原始資料的深複製
      this.originalClonedRows = this.clonedRows;
      const useBackupData =
        this.originalClonedRows[row.controls[useDataKey]?.getRawValue()];

      const useRightnowEditData = this.value[currentPageIndex]?.getRawValue();
      if (isNullOrEmpty(useRightnowEditData)) {
        return;
      }
      const useKeys = Object.keys(useRightnowEditData);
      let valueHasChanged = false;
      Object.values(useRightnowEditData)?.forEach(
        (element: any, index: number) => {
          if (useBackupData && useKeys?.[index]) {
            // 將兩邊的值都轉為字串比較,處理 null 或 undefined 的情況
            const oldValue = String(useBackupData[useKeys[index]] ?? '');
            const newValue = String(element ?? '');

            if (oldValue !== newValue) {
              valueHasChanged = true;
            }
          }
        }
      );
      if (valueHasChanged) {
        this.hintDialogService.showConfirmHint(
          'Library.UI.DataTable.enquiryConfirmationOfRowEditCancel',
          () => rollBackValue(),
          () => backToEdit()
        );
      }
      return;
    }
    rollBackValue();
  }

  useAddNewRow() {
    this.logger.debug('addNewRow', this.value);
    if (isNullOrEmpty(this.createRowFunc)) {
      throw new Error('createRowFunc is null.');
    }
    const newRow = this.createRowFunc!(this.index);
    if (isNullOrEmpty(newRow)) {
      return null;
    }
    if (this.complex && this.createWithDailog) {
      this.value = [...this.value, newRow];
      this.valueChange.emit(this.value);
      return newRow;
    }
    if (this.canDirectEdit) {
      newRow.create = true;
      newRow.editing = true;
      this.useOnRowEditInit(newRow, this.index);
      this.cd.detectChanges();
      this.logger.debug('useOnRowEditInit editing', newRow.editing);
    } else {
      newRow.create = true;
    }
    this.value?.push(newRow);
    this.logger.debug('add row', this.value, newRow);

    // 跳到最後一頁
    this.first =
      (Math.ceil(this.totalRecords / this.table._rows) - 1) * this.table._rows;
    this.table.first = this.first;
    this.firstChange.emit(this.first);
    this.cd.detectChanges();
    return newRow;
  }
  /**
   * 刪除按鈕 Click
   * button click event
   * @memberof DataTableComponent
   */
  useOnDeleteBtnClick(): void {
    if (this.validateDelete) {
      const useDataKey = this.dataKey.split('.')[1];
      const deleteKeys = (this.selection as any[]).map((x) =>
        x.controls[useDataKey]?.getRawValue()
      );
      const deletedRows = this.value.filter((x) =>
        deleteKeys.includes(x.controls[useDataKey]?.getRawValue())
      );
      if (!this.validateRowDeleteFunc(deletedRows)) {
        this.validateDeleteFailMsg.emit(true);
        return;
      }
    }
    if (this.showDeleteConfirmation) {
      const deleteMsg = isNullOrEmpty(this.deleteConfirmationMsg)
        ? 'Library.UI.DataTable.confirmToDelete'
        : this.deleteConfirmationMsg;

      this.hintDialogService.showConfirmHint(
        deleteMsg,
        () => {
          this.deleteRowsAction();
        },
        () => {
          return false;
        }
      );
      return;
    }
    this.deleteRowsAction();
  }

  deleteRowsAction() {
    const useDataKey = this.dataKey.split('.')[1];
    this.logger.debug('onDeleteBtnClick', this.value, this.selection);
    this.logger.debug('onDeleteBtnClick', this.value, this.selection);
    const deleteKeys = (this.selection as any[]).map((x) =>
      x.controls[useDataKey]?.getRawValue()
    );
    const useFormGroupArray = this.value as any;
    const deletedRows = useFormGroupArray.filter((x: FormGroup, index: any) =>
      deleteKeys.includes(x.controls[useDataKey]?.getRawValue())
    );
    this.value = this.value.filter(
      (x) =>
        deleteKeys.includes(x.controls[useDataKey]?.getRawValue()) === false
    );
    this.selection = [];
    this.selectionChange.emit(this.selection);
    this.valueChange.emit(this.value);
    this.rowDelete.emit(deletedRows);
    // 跳到正確頁(因為有時候刪掉列之後導致分頁消失)
    if (this.first >= this.totalRecords) {
      this.first =
        (Math.ceil(this.totalRecords / this.table._rows) - 1) *
        this.table._rows;
      this.table.first = this.first;
      this.firstChange.emit(this.first);
    }
  }
  /**
   * 判斷表格是否正在編輯中
   * @returns
   */
  validateTableEditing = (): boolean => {
    let checker = false;
    if (this.isTableEditing === true) {
      checker = true;
      this.hintDialogService.showErrorHint(
        'Library.UI.DataTable.CurrentlyEditing'
      );
    }
    return checker;
  };

  ngOnInit() {
    this.onRowEditInit = this.useOnRowEditInit;
    this.onRowEditSave = this.useOnRowEditSave;
    if (!isNullOrEmpty(this.validateRowEditFuncWithSync)) {
      this.onRowEditSave = this.useOnRowEditSaveWithSync;
    }
    this.onRowEditCancel = this.useOnRowEditCancel;
    this.addNewRow = this.useAddNewRow;
    this.onDeleteBtnClick = this.useOnDeleteBtnClick;
    this.translateService.onLangChange.subscribe({
      next: (event: any) => {
        this.logger.debug(
          'input-data-table-translate',
          event,
          this.translateService.currentLang
        );
      },
    });
  }
}
